import React, { useState } from "react";
import { urlParam } from "utils/helpers";
import "./styles.css";

import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";

import {
  makeSelectAccountSecondaryDetails,
  makeSelectEnableSimCountNextButton,
  makeSelectIsSecondaryDetailsCompleted,
  makeSelectSimCountNewAndTransfer
} from "ducks/selectors/planService";

import { setData } from "ducks/actions/index";

const SecondaryContact = ({
  onInfoClick,
  accountSecondaryDetails,
  setAccountSecondaryDetails,
  setIsSecondaryDetailsCompleted,
  isSecondaryDetailsCompleted
}) => {
  // Strip "+65" if present in initial state so input holds only 8 raw digits
  const initialMobile = (accountSecondaryDetails?.mobile_no || "")
    .replace("+65", "")
    .replace(/\D/g, "");

  const initialValues = {
    name: accountSecondaryDetails?.name || "",
    mobile_no: initialMobile,
    email: accountSecondaryDetails?.email || ""
  };

  const [formData, setFormData] = useState(initialValues);

  const [errors, setErrors] = useState({
    name: "",
    mobile_no: "",
    email: ""
  });

  const isValidEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // Allow only numbers for mobile number
    if (name === "mobile_no") {
      updatedValue = value.replace(/\D/g, "").slice(0, 8);
    }

    const updatedFormData = {
      ...formData,
      [name]: updatedValue
    };

    setFormData(updatedFormData);

    let currentError = "";
    if (name === "name") {
      if (!updatedValue.trim()) currentError = "Please fill in your full name";
    } else if (name === "mobile_no") {
      if (!updatedValue.trim()) {
        currentError = "Please fill in your mobile number";
      } else if (updatedValue.length !== 8) {
        currentError = "Please enter a valid mobile number";
      }
    } else if (name === "email") {
      if (!updatedValue.trim()) {
        currentError = "Please fill in your email address";
      } else if (!isValidEmail(updatedValue)) {
        currentError = "Please enter a valid email address";
      }
    }

    setErrors((prev) => ({
      ...prev,
      [name]: currentError
    }));

    const submittedFormData = {
      ...updatedFormData,
      mobile_no: updatedFormData.mobile_no ? `+65${updatedFormData.mobile_no}` : ""
    };

    setAccountSecondaryDetails(submittedFormData);

    const isNameValid = updatedFormData.name.trim() !== "";
    const isMobileValid = updatedFormData.mobile_no.length === 8;
    const isEmailValid = isValidEmail(updatedFormData.email);

    if (!(isNameValid && isMobileValid && isEmailValid)) {
      setIsSecondaryDetailsCompleted(false);
    } else if (urlParam("edit") === "true") {
      setIsSecondaryDetailsCompleted(true);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Please fill in your full name";
    }

    if (!formData.mobile_no.trim()) {
      newErrors.mobile_no = "Please fill in your mobile number";
    } else if (formData.mobile_no.length !== 8) {
      newErrors.mobile_no = "Please enter a valid mobile number";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Please fill in your email address";
    }

    setErrors(newErrors);

    // Don't continue if validation fails
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const submittedFormData = {
      ...formData,
      mobile_no: `+65${formData.mobile_no}`
    };

    setAccountSecondaryDetails(submittedFormData);
    setIsSecondaryDetailsCompleted(true);
  };

  const isFormValid = Boolean(
    formData.name.trim() !== "" &&
    formData.mobile_no.length === 8 &&
    isValidEmail(formData.email)
  );

  return (
    <div className="secondary-contact-container">
      <h2 className="secondary-contact-title">
        Secondary contact
      </h2>

      <p className="secondary-contact-desc">
        We’ll contact this person if we can’t reach the primary account holder.
      </p>

      <form
        onSubmit={handleSubmit}
        className="secondary-contact-form"
      >
        {/* Full Name */}
        <div className="form-group full-width">
          <label htmlFor="name" className="form-label">
            Full name as shown in NRIC/ID
          </label>

          <input
            type="text"
            id="name"
            name="name"
            className={`form-input ${
              errors.name ? "form-input-error" : ""
            }`}
            value={formData.name}
            onChange={handleChange}
            placeholder="Ong Lai Huat Apple"
          />

          {errors.name && (
            <div className="form-error">
              {errors.name}
            </div>
          )}
        </div>

        {/* Mobile & Email Row */}
        <div className="form-row">
          {/* Mobile */}
          <div className="form-group">
            <label htmlFor="mobile_no" className="form-label">
              Mobile number
            </label>

            <div
              className={`mobile-input-wrapper ${
                errors.mobile_no ? "mobile-wrapper-error" : ""
              }`}
            >
              <span className="country-code-fixed">+65</span>
              <input
                type="tel"
                id="mobile_no"
                name="mobile_no"
                className="mobile-input-field"
                value={formData.mobile_no}
                onChange={handleChange}
                inputMode="numeric"
                maxLength={8}
                placeholder="00000000"
              />
            </div>

            {errors.mobile_no && (
              <div className="form-error">
                {errors.mobile_no}
              </div>
            )}
          </div>

          {/* Email */}
          <div className="form-group">
            <label
              htmlFor="email"
              className="form-label label-with-info"
            >
              <span>Email address</span>

              <button
                type="button"
                className="info-btn"
                onClick={onInfoClick}
                aria-label="Email information"
              >
                i
              </button>
            </label>

            <input
              type="email"
              id="email"
              name="email"
              className={`form-input ${
                errors.email ? "form-input-error" : ""
              }`}
              value={formData.email}
              onChange={handleChange}
              placeholder="user@company.com"
            />

            {errors.email && (
              <div className="form-error">
                {errors.email}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isSecondaryDetailsCompleted && urlParam("edit") !== "true" && (
          <div className="form-actions">
            <button
              type="submit"
              className="next-btn"
              disabled={!isFormValid}
              style={{
                opacity: !isFormValid ? 0.5 : 1,
                cursor: !isFormValid ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>

            <span className="press-enter-hint">
              Press Enter
            </span>
          </div>
        )}
      </form>
    </div>
  );
};

export function mapDispatchToProps(dispatch) {
  return {
    setAccountSecondaryDetails: (accountSecondaryDetails) => {
      dispatch(
        setData({
          key: "accountSecondaryDetails",
          data: accountSecondaryDetails
        })
      );
    },

    setIsSecondaryDetailsCompleted: (isSecondaryDetailsCompleted) => {
      dispatch(
        setData({
          key: "isSecondaryDetailsCompleted",
          data: {
            isSecondaryDetailsCompleted
          }
        })
      );
    }
  };
}

const mapStateToProps = createStructuredSelector({
  simCounts: makeSelectSimCountNewAndTransfer(),
  enableSimCountNextBtn: makeSelectEnableSimCountNextButton(),
  accountSecondaryDetails: makeSelectAccountSecondaryDetails(),
  isSecondaryDetailsCompleted: makeSelectIsSecondaryDetailsCompleted()
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SecondaryContact);